import { ScrollableCellWithAvatar } from '@/components/data-table/cells/scrollable-cell';
import { getMemberStatusText, StatusBadge, StatusBadgeType } from '@/modules/common';
import { mapTeamMemberStatusFromDTO } from '@/modules/common/mappers/team-member-status.mapper';
import { TeamMemberStatusDTO } from '@/modules/common/types/team-member-status-dto';

/**
 * Minimal team-member shape the dropdown/filter label needs.
 * `status` is the raw DTO value (e.g. "ARCHIVED") coming from the short endpoint.
 */
export interface TeamMemberDropdownOption {
  label: string;
  avatar?: string | null;
  status?: string | null;
}

interface TeamMemberOptionContentProps {
  option: TeamMemberDropdownOption;
}

/**
 * Shared option row for team-member pickers: avatar + scroll-on-hover name + status badge.
 *
 * The name uses {@link ScrollableCellWithAvatar} so it stays within its container width
 * and scrolls on hover instead of forcing the row to grow (which would cause horizontal
 * scrolling). The status badge stays fixed beside it.
 *
 * The scheduled archive/unarchive warning icon is intentionally omitted because the short
 * team-members endpoint does not return scheduled-change dates (see OPS-1548 backend follow-up).
 */
const TeamMemberOptionContent = ({ option }: TeamMemberOptionContentProps) => {
  const status = option.status
    ? mapTeamMemberStatusFromDTO(option.status as TeamMemberStatusDTO)
    : null;

  return (
    <div className="flex w-full min-w-0 items-center justify-between gap-2 overflow-hidden">
      <ScrollableCellWithAvatar
        className="min-w-0 flex-1"
        text={option.label}
        avatarName={option.label}
        avatarSize="xs"
        image={option.avatar ?? undefined}
      />
      {status && (
        <div className="shrink-0">
          <StatusBadge
            type={StatusBadgeType.TeamMember}
            status={status}
            label={getMemberStatusText(status)}
          />
        </div>
      )}
    </div>
  );
};

/**
 * filters-v1 `CustomLabelComponent(value, option)` adapter for team-member pickers:
 * avatar + scroll-on-hover name + status badge.
 */
export const renderTeamMemberFilterLabel = (value: string, option: TeamMemberDropdownOption) => (
  <TeamMemberOptionContent option={{ ...option, label: value }} />
);
